/**
 * Devices — GET /api/v1/control/devices[/{device_id}] and the gate-mode switch, all
 * tenant-scoped by X-Company-Id.
 */
import {requestControl} from '@/shared/api';
import type {BulkOutcome} from '@/shared/api/bulk';
import type {
  DeviceCommandList,
  DeviceDetail,
  GateMode,
  PagedDevices,
  PagedProfiles,
  SettingsProfile,
  TerminalSettings,
} from '@/entities/device/types';

export type FetchDevicesParams = {
  search?: string;
  status?: string;
  page?: number;
  perPage?: number;
  /**
   * Fold the company's desktop bridges into the same list (2026-08-11). Off by
   * default, exactly as the route is: a screen that asks for terminals must not start
   * counting desktops because a library upgraded under it. `meta.total` follows the
   * flag, so a paginator does not have to correct for it.
   */
  includeDesktops?: boolean;
  /**
   * Filter to one department. This is a QUERY PARAMETER and not the `x-department-id`
   * header, which reads plausible and does nothing: the header is parsed by the
   * gateway but only for path segments that belong to a different product, and
   * `/control/*` derives its domain from `x-company-id` alone (conventions.md §2).
   */
  organizationDepartmentId?: string;
  /** `name` | `last_sync` | `created_at` | `updated_at` (sv_control's own vocabulary). */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export function fetchDevices(
  companyId: string,
  params: FetchDevicesParams = {},
): Promise<PagedDevices> {
  const qs = new URLSearchParams();
  if (params.search) {
    qs.set('search', params.search);
  }
  if (params.status) {
    qs.set('status', params.status);
  }
  if (params.page !== undefined) {
    qs.set('page', String(params.page));
  }
  if (params.perPage !== undefined) {
    qs.set('per_page', String(params.perPage));
  }
  if (params.includeDesktops) {
    qs.set('include_desktops', 'true');
  }
  if (params.organizationDepartmentId) {
    qs.set('organization_department_id', params.organizationDepartmentId);
  }
  if (params.sortBy) {
    qs.set('sort_by', params.sortBy);
  }
  if (params.sortOrder) {
    qs.set('sort_order', params.sortOrder);
  }
  const query = qs.toString();
  return requestControl<PagedDevices>(
    query ? `/control/devices?${query}` : '/control/devices',
    companyId,
  );
}

export function fetchDevice(
  companyId: string,
  deviceId: string,
): Promise<DeviceDetail> {
  return requestControl<DeviceDetail>(
    `/control/devices/${encodeURIComponent(deviceId)}`,
    companyId,
  );
}

/** PATCH /control/devices/{id}/gate-mode. The updated device is discarded. */
export async function setGateMode(
  companyId: string,
  deviceId: string,
  mode: GateMode,
): Promise<void> {
  await requestControl<unknown>(
    `/control/devices/${encodeURIComponent(deviceId)}/gate-mode`,
    companyId,
    {method: 'PATCH', body: JSON.stringify({gate_mode: mode})},
  );
}

/**
 * PATCH /control/devices/{id}/settings — write the per-device settings override with
 * the FULL settings object (it masks the assigned profile). The terminal pulls the
 * merged result on its next sync (pending_settings_sync is set server-side).
 */
export async function saveDeviceSettings(
  companyId: string,
  deviceId: string,
  settings: TerminalSettings,
): Promise<void> {
  await requestControl<unknown>(
    `/control/devices/${encodeURIComponent(deviceId)}/settings`,
    companyId,
    {method: 'PATCH', body: JSON.stringify({has_override: true, settings})},
  );
}

/**
 * Clear the per-device override so the assigned profile (or global default) resolves.
 * Used by "apply profile" so the profile actually wins over any prior override.
 */
export async function clearDeviceSettings(
  companyId: string,
  deviceId: string,
): Promise<void> {
  await requestControl<unknown>(
    `/control/devices/${encodeURIComponent(deviceId)}/settings`,
    companyId,
    {method: 'PATCH', body: JSON.stringify({has_override: false})},
  );
}

/**
 * GET /control/profiles — company settings presets.
 *
 * The envelope is not what this file used to assume. sv_control's own response names
 * the array `profiles`, but the gateway re-wraps list responses in its standard
 * `{meta, data}` — and the live API answers with `data` (verified against
 * api.sam-vision.uz, 2026-08-12). Reading only `.profiles`, as the profile picker did,
 * therefore found `undefined` and drew an empty list on a company that had presets.
 *
 * Both shapes are accepted and normalised to one, because the cost of guessing wrong
 * again is a screen that is silently, plausibly empty.
 */
export async function fetchProfiles(
  companyId: string,
): Promise<SettingsProfile[]> {
  const res = await requestControl<Partial<PagedProfiles> & {data?: SettingsProfile[]}>(
    '/control/profiles',
    companyId,
  );
  return res.data ?? res.profiles ?? [];
}

/** POST /control/devices/assign-profile — point devices at a profile. */
export async function assignProfile(
  companyId: string,
  deviceIds: string[],
  profileId: string,
): Promise<void> {
  await requestControl<unknown>('/control/devices/assign-profile', companyId, {
    method: 'POST',
    body: JSON.stringify({device_ids: deviceIds, profile_id: profileId}),
  });
}

/**
 * POST /control/devices/{id}/commands — queue a remote-control command. It executes on
 * the terminal's NEXT sync (up to the poll interval later), then acks back.
 */
export function enqueueCommand(
  companyId: string,
  deviceId: string,
  kind: string,
  params: Record<string, unknown> = {},
): Promise<{command_id: string}> {
  return requestControl<{command_id: string}>(
    `/control/devices/${encodeURIComponent(deviceId)}/commands`,
    companyId,
    {method: 'POST', body: JSON.stringify({kind, params})},
  );
}

/** GET /control/devices/{id}/commands — recent command history (newest first). */
export function fetchCommands(
  companyId: string,
  deviceId: string,
): Promise<DeviceCommandList> {
  return requestControl<DeviceCommandList>(
    `/control/devices/${encodeURIComponent(deviceId)}/commands`,
    companyId,
  );
}

/**
 * POST /control/devices/{id}/factory-reset — queue the wipe.
 *
 * The terminal acknowledges, waits out `grace_secs`, then erases its personnel,
 * biometric templates, local audit, licence and both server addresses, and
 * reboots into the pairing screen. Nothing here touches the cloud; call
 * {@link finalizeDeviceReset} for that once the command is acknowledged.
 */
export function resetDevice(
  companyId: string,
  deviceId: string,
  graceSecs?: number,
): Promise<{commandId: string; graceSecs: number}> {
  return requestControl<{commandId: string; graceSecs: number}>(
    `/control/devices/${encodeURIComponent(deviceId)}/factory-reset`,
    companyId,
    {
      method: 'POST',
      body: JSON.stringify(graceSecs ? {grace_secs: graceSecs} : {}),
    },
  );
}

/**
 * POST /control/devices/{id}/factory-reset/finalize — forget the device here.
 *
 * Releases every licence seat bound to this hardware and deletes the device's
 * rows; the company's attendance log is deliberately kept. Idempotent, and it
 * needs nothing from the terminal — so it also frees a device that is lost,
 * stolen or bricked.
 *
 * This is also what the plain "remove this terminal" button calls, and there is
 * deliberately no `deleteDevice` beside it: `DELETE /control/devices/{id}` does
 * the same removal while leaving the LICENCE SEAT occupied, with nothing in any
 * screen to say where the seat went. The route that cleans up after itself is
 * the only one this client offers for a single device.
 */
export function finalizeDeviceReset(
  companyId: string,
  deviceId: string,
): Promise<{revokedLicenseDevices: string[]}> {
  return requestControl<{revokedLicenseDevices: string[]}>(
    `/control/devices/${encodeURIComponent(deviceId)}/factory-reset/finalize`,
    companyId,
    {method: 'POST'},
  );
}

/** POST /control/profiles — create/update a preset (UPSERT keyed by profile_id). */
export function saveProfile(
  companyId: string,
  input: {
    profile_id?: string | null;
    name: string;
    description: string;
    payload: TerminalSettings;
    is_global_default: boolean;
  },
): Promise<SettingsProfile> {
  return requestControl<SettingsProfile>('/control/profiles', companyId, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** DELETE /control/profiles/{id}. Devices pointing at it fall back to the default. */
export function deleteProfile(
  companyId: string,
  profileId: string,
): Promise<void> {
  return requestControl<void>(
    `/control/profiles/${encodeURIComponent(profileId)}`,
    companyId,
    {method: 'DELETE'},
  );
}

/** PATCH /control/devices/{id}/name — the label everything else in the app shows. */
export async function renameDevice(
  companyId: string,
  deviceId: string,
  name: string,
): Promise<void> {
  await requestControl<unknown>(
    `/control/devices/${encodeURIComponent(deviceId)}/name`,
    companyId,
    {method: 'PATCH', body: JSON.stringify({name})},
  );
}

/**
 * PATCH /control/devices/{id}/department — move a device between departments.
 *
 * ⚠️ An id sv_control cannot resolve is stored as NULL and answered `Ok`, NOT as an
 * error (sv_control device_repo.rs: "fall back to NULL rather than storing the
 * unresolvable raw id"). So a department created seconds ago — before the
 * organization→control projection caught up — silently clears the field instead of
 * setting it, and the caller sees a success. Callers must re-read the device and
 * compare `department_id` before treating the move as done; `moveDeviceToDepartment`
 * below does exactly that rather than leaving it to each screen.
 *
 * Passing an empty string clears the department on purpose.
 */
async function patchDeviceDepartment(
  companyId: string,
  deviceId: string,
  departmentId: string,
): Promise<void> {
  await requestControl<unknown>(
    `/control/devices/${encodeURIComponent(deviceId)}/department`,
    companyId,
    {method: 'PATCH', body: JSON.stringify({department_id: departmentId})},
  );
}

/**
 * The department move, with the silent-NULL trap above closed: write, read back, and
 * if the field did not take, write once more — by then the projection has caught up,
 * which is the whole cause of the miss. Still not landed after the retry ⇒ throw, so
 * the screen says "не удалось" instead of showing a department that was never saved.
 */
export async function moveDeviceToDepartment(
  companyId: string,
  deviceId: string,
  departmentId: string,
): Promise<void> {
  await patchDeviceDepartment(companyId, deviceId, departmentId);

  const settled = (await fetchDevice(companyId, deviceId)).summary?.department_id ?? '';
  if (settled === departmentId) {
    return;
  }

  await patchDeviceDepartment(companyId, deviceId, departmentId);
  const retried = (await fetchDevice(companyId, deviceId)).summary?.department_id ?? '';
  if (retried !== departmentId) {
    throw new Error('department_not_applied');
  }
}

/** What a forced sync is allowed to re-send. Omitted ⇒ "full". */
export type SyncMode = 'full' | 'settings' | 'personnel';

/**
 * POST /control/devices/{id}/force-sync — mark the device as needing a re-send now
 * rather than at its next scheduled poll. Still asynchronous: the terminal picks it up
 * when it next reaches the server, which is what `pending_full_sync` on the card means.
 */
export async function forceSync(
  companyId: string,
  deviceId: string,
  mode: SyncMode = 'full',
): Promise<void> {
  await requestControl<unknown>(
    `/control/devices/${encodeURIComponent(deviceId)}/force-sync`,
    companyId,
    {method: 'POST', body: JSON.stringify({mode})},
  );
}

/**
 * ── Bulk device operations ───────────────────────────────────────────────────
 *
 * Same envelope and same 500-id cap as the personnel batches — see shared/api/bulk.
 * Two of them treat an explicit empty string as a deliberate CLEAR (`profile_id`,
 * `department_id`); that is the only way to ask for it, and it is why neither
 * parameter is optional here.
 */
export function bulkSetGateMode(
  companyId: string,
  ids: string[],
  gateMode: GateMode,
): Promise<BulkOutcome> {
  return requestControl<BulkOutcome>('/control/devices/bulk/gate-mode', companyId, {
    method: 'POST',
    body: JSON.stringify({ids, gate_mode: gateMode}),
  });
}

export function bulkAssignProfile(
  companyId: string,
  ids: string[],
  profileId: string,
): Promise<BulkOutcome> {
  return requestControl<BulkOutcome>(
    '/control/devices/bulk/assign-profile',
    companyId,
    {method: 'POST', body: JSON.stringify({ids, profile_id: profileId})},
  );
}

export function bulkSetDeviceDepartment(
  companyId: string,
  ids: string[],
  departmentId: string,
): Promise<BulkOutcome> {
  return requestControl<BulkOutcome>('/control/devices/bulk/department', companyId, {
    method: 'POST',
    body: JSON.stringify({ids, department_id: departmentId}),
  });
}

export function bulkForceSync(
  companyId: string,
  ids: string[],
  mode: SyncMode = 'full',
): Promise<BulkOutcome> {
  return requestControl<BulkOutcome>('/control/devices/bulk/force-sync', companyId, {
    method: 'POST',
    body: JSON.stringify({ids, mode}),
  });
}

/**
 * ⚠️ Leaves every licence seat occupied — see `finalizeDeviceReset`. No screen calls
 * this today, and one that wants to should loop `finalizeDeviceReset` instead unless
 * the seats are meant to stay bound to the hardware.
 */
export function bulkDeleteDevices(
  companyId: string,
  ids: string[],
): Promise<BulkOutcome> {
  return requestControl<BulkOutcome>('/control/devices/bulk/delete', companyId, {
    method: 'POST',
    body: JSON.stringify({ids}),
  });
}

/**
 * The batch twin of {@link enqueueCommand} — and NOT its equal: this route accepts
 * only the five commands it lists (`open_door`, `pulse_relay`, `set_led`,
 * `switch_camera`, `ota_update`), while the single-device route validates nothing at
 * all. Anything outside that set has to go one device at a time.
 */
export type BulkCommandKind =
  | 'open_door'
  | 'pulse_relay'
  | 'set_led'
  | 'switch_camera'
  | 'ota_update';

export function bulkCommand(
  companyId: string,
  ids: string[],
  command: BulkCommandKind,
  params: Record<string, unknown> = {},
): Promise<BulkOutcome> {
  return requestControl<BulkOutcome>('/control/devices/bulk/command', companyId, {
    method: 'POST',
    body: JSON.stringify({ids, command, params}),
  });
}

/**
 * POST /control/devices/{id}/account — set the password of an EXISTING account.
 *
 * Creating one is deliberately not possible from here: a terminal account is
 * created at the terminal, in front of the person it belongs to. The cloud can
 * only change a password and revoke — sv_gateway refuses an unknown username.
 *
 * The gateway turns this into a queued `set_device_password` command, so it
 * takes effect on the board's next sync, not on the response.
 */
export async function setDeviceAccountPassword(
  companyId: string,
  deviceId: string,
  username: string,
  password: string,
): Promise<void> {
  await requestControl<unknown>(
    `/control/devices/${encodeURIComponent(deviceId)}/account`,
    companyId,
    {method: 'POST', body: JSON.stringify({username, password})},
  );
}

/**
 * DELETE /control/devices/{id}/account/{username} — revoke an account.
 *
 * Also a queued command. The board refuses to remove its last administrator,
 * which is the real guarantee; the card only greys the button as a hint.
 */
export async function revokeDeviceAccount(
  companyId: string,
  deviceId: string,
  username: string,
): Promise<void> {
  await requestControl<unknown>(
    `/control/devices/${encodeURIComponent(deviceId)}/account/${encodeURIComponent(username)}`,
    companyId,
    {method: 'DELETE'},
  );
}

/**
 * GET /control/devices/{id}/lan-key — the credential a desktop bridge presents to the
 * terminal's own HTTP API on the local network.
 *
 * Three things about this route are unlike the rest of the device family, and all
 * three shape how a screen may use it.
 *
 * **It needs `devices/manage`, not the `read` a device card takes.** Handing out a
 * key that opens a door is deliberately not the same right as looking at the card it
 * sits behind, so a member who can see this terminal may still get a 403 here.
 *
 * **Platform administrators are excluded on purpose** — the key belongs to the company
 * that owns the board, not to whoever operates the platform.
 *
 * **`null` means "the terminal has not said", not "there is no key".** Only 2026-08-20
 * builds and later report it; anything older answers null forever, and a screen that
 * renders that as «нет ключа» is telling the owner their bridge cannot work when in
 * fact nobody has asked the board yet.
 *
 * Never log it and never put it in a list: it is shown once, where somebody asked.
 */
export async function fetchDeviceLanKey(
  companyId: string,
  deviceId: string,
): Promise<string | null> {
  const res = await requestControl<{lan_key?: string | null; key?: string | null}>(
    `/control/devices/${encodeURIComponent(deviceId)}/lan-key`,
    companyId,
  );
  // The route documents no schema for its 200 body, so both spellings are read rather
  // than betting on one. An empty string is the same silence as a null.
  return res.lan_key || res.key || null;
}

/**
 * DELETE /control/devices/{id} — remove a device's rows in sv_control.
 *
 * ⚠️ **Not the button an owner wants, and not what the cabinet's UI calls.** This
 * clears the sv_control rows and LEAVES THE LICENCE SEAT OCCUPIED. Remove and re-adopt
 * four terminals this way and four seats are silently gone, with no screen anywhere to
 * explain where. `finalizeDeviceReset` does the same removal AND releases the seats,
 * works on a board that is lost or bricked, and is idempotent — see the note atop
 * `features/manage-device/ui/DeviceAdminPanel.tsx` for why it is wired there instead.
 *
 * Kept here because it is part of the surface and a caller who genuinely means "drop
 * the row, leave the licence alone" — a stale desktop-bridge row, which holds no seat
 * to begin with — has no other way to say it. In the `system` domain an id present in
 * more than one company answers 409 `ambiguous_entity` with the candidates in
 * `details.candidates`; from the cabinet, which always sends a company, it cannot.
 */
export function deleteDevice(
  companyId: string,
  deviceId: string,
): Promise<void> {
  return requestControl<void>(
    `/control/devices/${encodeURIComponent(deviceId)}`,
    companyId,
    {method: 'DELETE'},
  );
}
